import {hpcAttrs} from '@github-ui/hpc'
import {Fragment, useEffect, useRef, useState} from 'react'
import type {ReactNode} from 'react'
import type {Repository} from '@github-ui/current-repository'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import type {HomeRoutePayload, Ruleset} from '../types/rules-types'
import {Blankslate} from '../components/Blankslate'
import {BorderBox} from '../components/BorderBox'
import {BranchSelector} from '../components/BranchSelector'
import {Subhead} from '../components/Subhead'
import {RulesetList} from '../components/RulesetList'
import {FlashUpsellBanner, UpsellBanner} from '../components/UpsellBanner'
import {useRelativeNavigation} from '../hooks/use-relative-navigation'
import {NewRulesetButton} from '../components/NewRulesetButton'
import {capitalize} from '../helpers/string'
import {sortSourceTypes} from '../helpers/utils'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useIsStafftools} from '../hooks/use-is-stafftools'
import {DismissibleFlashOrToast, type FlashAlert} from '@github-ui/dismissible-flash'
import {useRuleStrings} from '../hooks/use-rule-strings'
import {useUpsellInfoContext} from '../contexts/UpsellInfoContext'
import {RuleConfigList} from '@github-ui/rule-config-list'
import {Flash, Link} from '@primer/react'

import styles from './OverviewPage.module.css'
import {useRuleFeatures} from '../hooks/use-rule-features'

// Matches the canonical `Learn more: <url>` tail on the flash message so the URL
// can be rendered as an anchor while the rest of the message passes through the backtick tokenizer.
const TRAILING_LEARN_MORE_REGEX = /^([\s\S]*?)\s+Learn more:\s+(\S+)\s*$/
const BACKTICK_REGEX = /`([^`]+)`/g

function renderFlashMessage(message: string): ReactNode {
  const trailingMatch = message.match(TRAILING_LEARN_MORE_REGEX)
  const messageBody = trailingMatch ? trailingMatch[1]! : message
  const learnMoreUrl = trailingMatch?.[2]

  const messageParts = messageBody.split(BACKTICK_REGEX).map((part, index) =>
    // The split result is positionally stable: even indexes are plain text,
    // odd indexes are the backtick-wrapped tokens. Index is a valid key here.
    // eslint-disable-next-line @eslint-react/no-array-index-key
    index % 2 === 1 ? <code key={index}>{part}</code> : part,
  )

  if (!learnMoreUrl) {
    return <>{messageParts}</>
  }

  return (
    <>
      {messageParts}{' '}
      <Link
        href={learnMoreUrl}
        className="Link--inTextBlock"
        aria-label="Learn more about pull_request_target restrictions"
      >
        Learn more
      </Link>
    </>
  )
}

export function OverviewPage() {
  const {
    source,
    sourceType,
    rulesets,
    matchingRulesets,
    editableRulesets,
    branch,
    branchListCacheKey,
    readOnly,
    paths,
    flashMessages = [],
  } = useRoutePayload<HomeRoutePayload>()
  const {resolvePath} = useRelativeNavigation()
  const upsellInfo = useUpsellInfoContext()
  const [flashAlert, setFlashAlert] = useState<FlashAlert>({message: '', variant: 'default'})
  const flashRef = useRef<HTMLDivElement | null>(null)
  const isStafftools = useIsStafftools()
  const isRuleConfigListEnabled = isFeatureEnabled('rule_config_list') || isStafftools

  const {heading, alphaOrBeta, headingText, rulesetOrPolicy, rulesetsOrPolicies} = useRuleStrings()
  const {supportedTargets} = useRuleFeatures()
  const supportsGitRefTargets = supportedTargets.includes('branch') || supportedTargets.includes('tag')

  useEffect(() => {
    flashRef.current?.focus()
  }, [flashAlert, flashRef])

  const rulesetsBySource = rulesets.reduce<Record<string, Ruleset[]>>((map, item) => {
    // if a branch/ref is specified for a supported git-ref target, filter based on whether each ruleset is applied
    if (supportsGitRefTargets && branch && !matchingRulesets.includes(item.id)) {
      return map
    }

    const key = item.source.type
    if (!map[key]) {
      map[key] = []
    }

    map[key].push(item)
    return map
  }, {})

  const inheritedSourceTypes = Object.keys(rulesetsBySource).sort(sortSourceTypes)

  const hasFilteredRulesets = rulesets.length > 0 && inheritedSourceTypes.length === 0

  return (
    /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
    <div className="settings-next position-relative" {...hpcAttrs()}>
      <Subhead
        heading={heading}
        alphaOrBeta={alphaOrBeta}
        renderAction={() => {
          return readOnly ? null : rulesets.length > 0 ? (
            <NewRulesetButton
              rulesetsUrl={resolvePath('./')}
              setFlashAlert={setFlashAlert}
              className={styles.NewRulesetButton}
            />
          ) : null
        }}
      />
      <DismissibleFlashOrToast flashAlert={flashAlert} setFlashAlert={setFlashAlert} ref={flashRef} />
      <div className="d-flex flex-column gap-3">
        {flashMessages.map(flashMessage =>
          flashMessage.message ? (
            <Flash key={flashMessage.message} variant="default">
              {renderFlashMessage(flashMessage.message)}
            </Flash>
          ) : null,
        )}
        {upsellInfo.rulesets.cta.visible && inheritedSourceTypes.length > 0 && (
          <FlashUpsellBanner ctaType="rulesets" sourceType={sourceType} />
        )}
        {sourceType === 'repository' && supportsGitRefTargets && rulesets.length > 0 ? (
          <div>
            <BranchSelector gitRef={branch} repo={source as Repository} branchListCacheKey={branchListCacheKey} />
          </div>
        ) : null}
        {inheritedSourceTypes.length > 0 ? (
          inheritedSourceTypes.map(key => (
            <Fragment key={key}>
              <BorderBox
                name={`${capitalize(key)} ${rulesetsOrPolicies}`}
                showHeader={inheritedSourceTypes.length > 1 || !inheritedSourceTypes.includes(capitalize(sourceType))}
                renderCreateButton={() => {
                  // Don't show managed by if the source type is the same as the group's type
                  if (sourceType === key.toLowerCase()) {
                    return null
                  }

                  const ownedByName = rulesetsBySource[key]![0]!.source.name
                  return <div className="text-small color-fg-muted mt-1">Managed by {ownedByName}</div>
                }}
              >
                <RulesetList
                  rulesets={rulesetsBySource[key]!}
                  sourceType={sourceType}
                  source={source}
                  readOnly={readOnly}
                  setFlashAlert={setFlashAlert}
                />
              </BorderBox>

              {!readOnly &&
                key.toLowerCase() !== sourceType &&
                editableRulesets.includes(rulesetsBySource[key]![0]!.id) && (
                  <div className="text-small">
                    <span className="text-bold">Tip:</span>{' '}
                    <span className="color-fg-muted">
                      As a {rulesetsBySource[key]![0]?.source.name} admin, you can
                      <a href={rulesetsBySource[key]![0]?.source.url}>
                        {' '}
                        manage {source.name} {rulesetsOrPolicies} in {key.toLocaleLowerCase()} settings.
                      </a>
                    </span>
                  </div>
                )}
            </Fragment>
          ))
        ) : (
          <>
            {hasFilteredRulesets ? (
              <Blankslate heading={`No ${rulesetsOrPolicies} matched your search`}>
                {`Try expanding your search or creating a new ${rulesetOrPolicy}`}
              </Blankslate>
            ) : (
              <UpsellBanner
                headingText={headingText}
                withBorder={false}
                showNewRulesetButton={!readOnly}
                infoVariant="two"
                sourceType={sourceType}
                ctaType="rulesets"
                setFlashAlert={setFlashAlert}
              />
            )}
          </>
        )}
        {isRuleConfigListEnabled && branch && paths?.configList ? (
          <RuleConfigList refName={branch} path={paths.configList} />
        ) : null}
      </div>
    </div>
  )
}
